import React from "react";
import { ScrollText } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Page Conditions Générales de Vente (CGV).
 * Texte fourni par le client — version FR canonique. Les autres langues
 * tomberont sur le FR par défaut tant qu'une traduction officielle n'est
 * pas validée par Luxury Signs Sàrl.
 */

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`cgv-section-${n}`}>
    <h2 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wider text-[#39FF14] neon-text-soft mb-4 flex items-baseline gap-3">
      <span className="font-mono text-sm text-zinc-500">{String(n).padStart(2, "0")}.</span>
      {title}
    </h2>
    <div className="text-zinc-300 text-base leading-relaxed font-body space-y-3 pl-10 border-l border-white/5">
      {children}
    </div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#39FF14]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export default function CGV() {
  const { lang } = useI18n();

  return (
    <div className="min-h-screen bg-black text-white" data-testid="page-cgv">
      {/* Hero */}
      <section className="pt-28 lg:pt-32 pb-14 border-b border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#39FF14]" style={{ boxShadow: "0 0 12px #39FF14" }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
              {lang === "fr" ? "Mentions légales" : lang === "de" ? "Rechtliches" : lang === "it" ? "Note legali" : "Legal"}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight">
            <ScrollText className="inline-block mb-2 mr-3 text-[#39FF14]" size={42} />
            {lang === "fr" ? "Conditions générales" : lang === "de" ? "Allgemeine Geschäftsbedingungen" : lang === "it" ? "Condizioni generali" : "Terms & conditions"}
            <span className="block mt-1 text-zinc-400 text-2xl sm:text-3xl">
              {lang === "fr" ? "de vente (CGV)" : lang === "de" ? "(AGB)" : lang === "it" ? "di vendita (CGV)" : "of sale"}
            </span>
          </h1>
          <p className="mt-6 text-base text-zinc-400 max-w-2xl">
            {lang === "fr"
              ? "Version applicable aux ventes et services réalisés via le site signapharma.ch. Texte original en français — version contractuelle de référence."
              : "Version applicable to all sales made through signapharma.ch. The French version is the legally binding reference text."}
          </p>
        </div>
      </section>

      {/* Contenu */}
      <section className="py-14 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">

          <Section n={1} title="Société">
            <p>Le site SignaPharma est exploité par :</p>
            <p className="font-mono text-sm">
              <strong className="text-white">SIGNAPHARMA – Luxury Signs Sàrl</strong><br />
              Frontenex 8<br />
              1207 Genève – Suisse<br />
              Téléphone : <a href="tel:+41762223603" className="text-[#39FF14] hover:underline">+41 76 222 36 03</a><br />
              E-mail : <a href="mailto:info@signarama.ch" className="text-[#39FF14] hover:underline">info@signarama.ch</a>
            </p>
            <p>
              SIGNAPHARMA est spécialisé dans la fabrication et la vente d'enseignes lumineuses,
              lettres lumineuses, croix de pharmacie LED, logos rétroéclairés et signalétique sur mesure.
            </p>
          </Section>

          <Section n={2} title="Objet">
            <p>
              Les présentes Conditions Générales de Vente définissent les droits et obligations
              entre SIGNAPHARMA et toute personne physique ou morale effectuant un achat via le
              site internet.
            </p>
            <p>Toute commande implique l'acceptation pleine et entière des présentes CGV.</p>
          </Section>

          <Section n={3} title="Produits">
            <p>Les produits proposés sont fabriqués sur mesure ou personnalisés selon les demandes du client :</p>
            <Bullets items={[
              "Croix de pharmacie lumineuses",
              "Lettres boîtiers LED",
              "Logos lumineux",
              "Enseignes intérieures et extérieures",
              "Caducées lumineux",
              "Signalétique professionnelle",
              "Néons LED personnalisés",
            ]} />
            <p>
              Les visuels, dimensions et couleurs présentés sur le site sont non contractuels et
              peuvent légèrement varier selon les matériaux, écrans ou contraintes techniques de
              fabrication.
            </p>
          </Section>

          <Section n={4} title="Commande">
            <p>Toute commande devient définitive après :</p>
            <Bullets items={[
              "Validation du panier par le client",
              "Acceptation des présentes Conditions Générales de Vente",
              "Réception et validation du paiement",
            ]} />
            <p>Une confirmation de commande peut être envoyée par e-mail après validation.</p>
            <p>SIGNAPHARMA se réserve le droit de refuser ou d'annuler toute commande en cas :</p>
            <Bullets items={[
              "D'informations incomplètes ou erronées",
              "De problème de paiement",
              "D'impossibilité technique de fabrication",
              "De suspicion de fraude ou litige antérieur",
            ]} />
            <p>
              Les maquettes, aperçus et visuels transmis au client doivent être vérifiés avant
              validation définitive de fabrication.
            </p>
          </Section>

          <Section n={5} title="Prix">
            <p>Tous les prix affichés sur le site sont indiqués exclusivement en francs suisses (CHF).</p>
            <p>
              Les prix peuvent être affichés hors taxes (HT) ou toutes taxes comprises (TTC) selon
              le statut du client et les mentions indiquées sur le site ou le devis.
            </p>
            <p>
              Les frais de livraison, d'installation ou prestations complémentaires éventuelles
              sont indiqués séparément avant validation finale de la commande.
            </p>
            <p>
              SIGNAPHARMA se réserve le droit de modifier ses tarifs à tout moment, sans impact
              sur les commandes déjà validées et payées.
            </p>
          </Section>

          <Section n={6} title="Paiement">
            <p>Les paiements peuvent être effectués par :</p>
            <Bullets items={[
              "Virement bancaire",
              "Carte bancaire",
              "TWINT",
              "Solutions de paiement partenaires",
            ]} />
            <p>Selon le type de fabrication, un acompte peut être exigé avant lancement de production.</p>
            <p>En cas de retard de paiement, la commande peut être suspendue ou annulée.</p>
          </Section>

          <Section n={7} title="Fabrication et délais">
            <p>
              Les produits étant majoritairement fabriqués sur mesure, les délais de fabrication
              et d'expédition sont généralement compris entre :
            </p>
            <div className="my-4 inline-block px-5 py-3 border border-[#39FF14]/40 bg-[#39FF14]/5">
              <span className="font-display text-2xl font-bold text-[#39FF14] neon-text-soft">
                3 à 5 semaines
              </span>
            </div>
            <p>Les délais sont donnés à titre indicatif et peuvent varier selon :</p>
            <Bullets items={[
              "La complexité du projet",
              "La validation des fichiers",
              "Les périodes de forte activité",
              "Les contraintes fournisseurs ou transporteurs",
            ]} />
            <p>Un retard raisonnable ne peut donner lieu à annulation, remboursement ou indemnisation.</p>
          </Section>

          <Section n={8} title="Livraison">
            <p>SIGNAPHARMA expédie ses produits dans toute la Suisse.</p>
            <p>
              Les livraisons sont effectuées par transporteur spécialisé ou service postal selon
              les dimensions des produits.
            </p>
            <p>
              Le client est tenu de vérifier l'état du colis à réception et de signaler toute
              anomalie sous 48 heures avec photos à l'appui.
            </p>
            <p>Les risques liés au transport sont transférés au client dès la livraison.</p>
          </Section>

          <Section n={9} title="Installation">
            <p>Sauf mention contraire, les produits sont vendus sans pose.</p>
            <p>Le client est responsable :</p>
            <Bullets items={[
              "Des autorisations administratives",
              "Des raccordements électriques",
              "Du respect des normes locales",
              "De la conformité du support de fixation",
            ]} />
            <p>Toute installation doit être réalisée par un professionnel qualifié.</p>
          </Section>

          <Section n={10} title="Garantie">
            <p>
              Les produits bénéficient d'une garantie limitée couvrant les défauts de fabrication
              dans le cadre d'une utilisation normale.
            </p>
            <p>La garantie ne couvre pas :</p>
            <Bullets items={[
              "Mauvaise installation",
              "Choc ou casse",
              "Surtension électrique",
              "Humidité excessive",
              "Modification du produit",
              "Usure normale",
            ]} />
            <p>Les LED ont une durée de vie indicative pouvant atteindre 50'000 heures selon utilisation.</p>
          </Section>

          <Section n={11} title="Produits personnalisés">
            <p>
              Les produits personnalisés ou fabriqués sur mesure ne sont ni repris ni remboursés
              après validation de fabrication.
            </p>
            <p>Le client est responsable de la conformité des fichiers, logos et textes transmis.</p>
          </Section>

          <Section n={12} title="Propriété intellectuelle">
            <p>
              Les maquettes, visuels, concepts et créations réalisés par SIGNAPHARMA restent la
              propriété exclusive de Luxury Signs Sàrl jusqu'au paiement intégral des sommes dues.
            </p>
            <p>Toute reproduction ou utilisation sans autorisation écrite est interdite.</p>
          </Section>

          <Section n={13} title="Responsabilité">
            <p>SIGNAPHARMA ne pourra être tenu responsable :</p>
            <Bullets items={[
              "Des pertes d'exploitation",
              "Des retards liés aux transporteurs",
              "Des dommages indirects",
              "D'une mauvaise utilisation des produits",
            ]} />
            <p>La responsabilité totale de SIGNAPHARMA est limitée au montant de la commande concernée.</p>
          </Section>

          <Section n={14} title="Données personnelles">
            <p>
              Les informations collectées sont utilisées uniquement dans le cadre du traitement
              des commandes et de la relation commerciale.
            </p>
            <p>
              SIGNAPHARMA s'engage à protéger les données personnelles conformément au droit
              suisse applicable.
            </p>
          </Section>

          <Section n={15} title="Droit applicable">
            <p>Les présentes CGV sont soumises au droit suisse.</p>
            <p>
              En cas de litige, les tribunaux compétents seront ceux du canton de Genève, sauf
              disposition légale contraire.
            </p>
          </Section>

          {/* Footer date */}
          <div className="mt-16 pt-8 border-t border-white/10 font-mono text-xs uppercase tracking-widest text-zinc-500 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span>Signapharma — Luxury Signs Sàrl</span>
            <span>CGV applicables — version {new Date().getFullYear()}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
